import { HaNRoomCellProps } from "./types";
import { useTranslation } from "react-i18next";

import { pickUsingStringAsIndex } from "@lib/utils";
import { useRoomImage } from "@hooks";

import { useTheme } from "styled-components";
import { StyledModalCellItemIcon, StyledTruncatedText, Wrapper } from "./styles";
import { FlexCol, FlexRow, P } from "@quarks";
import { HaNScheduleDataIcon } from "@organisms";
import { useScheduleAndSensorStatusesForRequest } from "@lib/logic";
import { useActiveMeetingRoomRequest, useHereAndNow } from "@pages";

/**
 * Room cell for list of rooms on Here And Now.
 * Adapts to small screen size and is used in both `RoomsList` and `RoomsListHorizontal`.
 */

export const HaNRoomCell = ({ index, room, onClick }: HaNRoomCellProps) => {
  const { hoverRoomId, selectedObject } = useHereAndNow();
  const { t } = useTranslation();
  const theme = useTheme();
  const { displayName, capacity, floorName, roomPictureHash, buildingId, email, id } = room;
  const color = pickUsingStringAsIndex(email, theme.avatar.colors);
  const imageURL = useRoomImage(
    email || displayName,
    roomPictureHash,
    buildingId,
    Math.round(48 * window.devicePixelRatio * 4),
  );
  const request = useActiveMeetingRoomRequest({
    buildingId,
    selectedFloorId: room.floorId ?? null,
  });
  const { scheduleStatus, sensorStatus, sublineText } = useScheduleAndSensorStatusesForRequest(request, id, email);

  return (
    <Wrapper
      as="button"
      tabIndex={1}
      onClick={() => onClick(room)}
      data-hover={hoverRoomId === room.id || selectedObject?.id === room.id}
      onKeyDown={e => {
        if (e.key === "Enter") onClick(room);
      }}>
      <div className="flex flex-col gap-2 w-full">
        <div className="flex justify-between items-center ">
          <FlexRow
            gap={8}
            className="gap-3"
            alignItems="flex-start"
            maxWidth="250px"
            sm={{ maxWidth: 225 }}>
            <StyledModalCellItemIcon
              imageURL={imageURL}
              backgroundColor={color}
              icon="meetingRoom"
            />
            <FlexCol
              justifyContent="center"
              overflow="hidden"
              paddingTop={2}>
              <StyledTruncatedText fontWeight="bold">{displayName}</StyledTruncatedText>
              <P>
                {t("meeting:MeetingRoomBasicInfo", {
                  count: capacity,
                  floorName: floorName,
                })}
              </P>
              {sublineText && <StyledTruncatedText color={theme.text.color.error}>{sublineText}</StyledTruncatedText>}
            </FlexCol>
          </FlexRow>
          <FlexRow gap={8}>
            {sensorStatus && (
              <HaNScheduleDataIcon
                data-testid={`HNSensorDataIcon-${index}`}
                status={sensorStatus}
                icon="broadcast"
              />
            )}
            {scheduleStatus && (
              <HaNScheduleDataIcon
                data-testid={`HNScheduleDataIcon-${index}`}
                status={scheduleStatus}
                icon="calendar"
              />
            )}
          </FlexRow>
        </div>
      </div>
    </Wrapper>
  );
};
